import React, { useState, useRef, useEffect } from 'react';
import { 
  Paper, Typography, Box, ButtonGroup, 
  IconButton, Stack, Divider, useTheme, Button
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VideocamIcon from '@mui/icons-material/Videocam';
import SwitchCameraIcon from '@mui/icons-material/SwitchCamera';

// Video sources
const videoSources = {
  pov: 'videos/pov.mp4',
  external: 'videos/ext.mp4'
};

const LiveFeed = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [currentVideo, setCurrentVideo] = useState('pov');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const povVideoRef = useRef(null);
  const extVideoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const theme = useTheme();
  
  // Handle video playback control and synchronization
  useEffect(() => {
    const extVideo = povVideoRef.current;
    const povVideo = extVideoRef.current;
    
    if (povVideo && extVideo) {
      // Apply playback state to both videos
      if (isPlaying) {
        povVideo.play();
        extVideo.play();
      } else {
        povVideo.pause();
        extVideo.pause();
      }
      
      // Synchronize videos
      const syncVideos = () => {
        // Only sync if the time difference is more than 0.1 second
        if (Math.abs(povVideo.currentTime - extVideo.currentTime) > 0.1) {
          extVideo.currentTime = povVideo.currentTime;
        }
      };
      
      // Listen for timeupdate to keep videos in sync
      povVideo.addEventListener('timeupdate', syncVideos);
      
      return () => {
        povVideo.removeEventListener('timeupdate', syncVideos);
      };
    }
  }, [isPlaying]);
  
  // When videos are loaded, ensure they start in sync
  useEffect(() => {
    const povVideo = povVideoRef.current;
    const extVideo = extVideoRef.current;
    
    if (povVideo && extVideo) {
      const handleLoadedData = () => {
        // Once both videos are loaded, synchronize their start times
        if (povVideo.readyState >= 2 && extVideo.readyState >= 2) {
          extVideo.currentTime = povVideo.currentTime;
        }
      };
      
      povVideo.addEventListener('loadeddata', handleLoadedData);
      extVideo.addEventListener('loadeddata', handleLoadedData);
      
      return () => {
        povVideo.removeEventListener('loadeddata', handleLoadedData);
        extVideo.removeEventListener('loadeddata', handleLoadedData);
      };
    }
  }, []);

  // Monitor fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Toggle between POV and external camera
  const toggleCamera = () => {
    setCurrentVideo(currentVideo === 'pov' ? 'external' : 'pov');
  };
  
  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    const container = videoContainerRef.current;
    
    if (!container) return;
    
    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) { // Safari
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) { // IE11
        container.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) { // Safari
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) { // IE11
        document.msExitFullscreen();
      }
    }
  };
  
  return (
    <Paper 
      elevation={10} 
      sx={{ 
        p: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        width: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'rgba(30, 30, 40, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VideocamIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {currentVideo === 'pov' ? 'POV Camera' : 'External Camera'}
          </Typography>
          <Button 
            startIcon={<SwitchCameraIcon />}
            variant="outlined"
            size="small"
            onClick={toggleCamera}
            sx={{ 
              ml: 2,
              borderRadius: '8px',
              color: theme.palette.bmwAccent.lightBlue,
              borderColor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                borderColor: theme.palette.bmwAccent.lightBlue,
                bgcolor: 'rgba(33, 150, 243, 0.1)'
              }
            }}
          >
            Switch Camera
          </Button>
        </Box>
        <Typography 
          variant="caption" 
          sx={{ 
            px: 1.5, 
            py: 0.5, 
            borderRadius: '12px', 
            bgcolor: isPlaying ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
            color: isPlaying ? '#4CAF50' : '#F44336',
            fontWeight: 600,
            border: isPlaying ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(244, 67, 54, 0.3)',
          }}
        >
          {isPlaying ? 'LIVE' : 'PAUSED'}
        </Typography>
      </Box>
      
      {/* Video Feed Area */}
      <Box 
        ref={videoContainerRef}
        sx={{ 
          flex: 1,
          bgcolor: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          minHeight: '250px',
          maxHeight: isFullscreen ? 'none' : '500px'
        }}
      >
        {/* Video container with size constraints */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            maxWidth: isFullscreen ? 'none' : '800px',
            maxHeight: isFullscreen ? 'none' : '450px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {/* POV video element */}
          <video
            ref={povVideoRef}
            src={videoSources.pov}
            style={{ 
              width: '100%', 
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: isFullscreen ? 'cover' : 'contain',
              display: currentVideo === 'pov' ? 'block' : 'none'
            }}
            autoPlay={isPlaying}
            loop
            muted
          />
          
          {/* External camera video element */}
          <video
            ref={extVideoRef}
            src={videoSources.external}
            style={{ 
              width: '100%', 
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: isFullscreen ? 'cover' : 'contain',
              display: currentVideo === 'external' ? 'block' : 'none'
            }}
            autoPlay={isPlaying}
            loop
            muted
          />
        </Box>
      </Box>
      
      {/* Video Controls */}
      <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.3)' }}>
        <Stack 
          direction="row" 
          spacing={2} 
          justifyContent="space-between" 
          alignItems="center"
        >
          {/* Left side - now empty */}
          <Box></Box>
          
          <ButtonGroup 
            variant="contained" 
            size="small" 
            sx={{ 
              '& .MuiButtonGroup-grouped': {
                borderRadius: '8px',
                mx: 0.5,
                border: 'none',
                bgcolor: 'rgba(30, 30, 30, 0.6)',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: 'rgba(50, 50, 50, 0.8)',
                }
              }
            }}
          >
            <IconButton 
              size="small"
              sx={{ 
                color: '#fff',
                p: 1
              }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small"
              sx={{ 
                color: '#fff',
                p: 1
              }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small"
              onClick={toggleFullscreen}
              sx={{ 
                color: theme.palette.bmwAccent.lightBlue,
                p: 1
              }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </ButtonGroup>
        </Stack>
      </Box>
    </Paper>
  );
};

export default LiveFeed;
